import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { useLocale } from '../../context/LocaleContext';

const UserDeactivationDialog = ({ 
  open, 
  onClose, 
  user, 
  onSuccess,
  isDeactivating = false 
}) => {
  const { t } = useLocale();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      await onSuccess(user.id, isDeactivating, reason);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const isCurrentlyActive = user.status !== 'inactive';
  const actionText = isCurrentlyActive ? 'Deactivate' : 'Activate';
  const dialogTitle = isCurrentlyActive ? 'Deactivate User' : 'Activate User';
  const confirmText = isCurrentlyActive ? 'Deactivate' : 'Activate';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {dialogTitle}: {user.name || user.email}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isCurrentlyActive 
              ? 'This user will no longer be able to access the system. You can reactivate them later if needed.'
              : 'This user will regain access to the system.'
            }
          </Typography>
          
          {isCurrentlyActive && (
            <TextField
              fullWidth
              label="Reason for deactivation (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={3}
              placeholder="e.g., User left organization, temporary suspension, etc."
              sx={{ mb: 2 }}
            />
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isCurrentlyActive ? 'error' : 'primary'}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDeactivationDialog;
